// ─── Agent Directory ─────────────────────────────────────────────────────────
//
// The interactive Agent Directory layers human-readable "who talks to whom"
// metadata on top of the org-chart roster in `agent-org.ts`. The org chart is
// the single source of truth for the roster, models, accents and phase; this
// file ONLY adds the directory-specific fields the org chart doesn't carry:
//
//   - repo         which codebase the agent runs in (runtime vs product)
//   - reportsTo    Mark / Zeus / Athena
//   - identity     the Greek-pantheon one-liner
//   - cron         human-readable schedule lines (sourced from the *.skill.ts
//                  files in this repo; Guardians run on Inngest in the product
//                  repo so their schedules are described, not enumerated)
//   - consumesFrom upstream agents / APIs this agent reads from
//   - feedsInto    downstream agents this agent writes to
//
// Cron lines for Council agents + the two executives are transcribed from the
// `triggers.cron[].description` fields of their skill configs. Guardian and
// infrastructure schedules are described at the repo level because their skill
// definitions live in tmagent-product, not here.

import { ORG_BRANCHES, type AgentPhase, type OrgAgent } from './agent-org'

export type DirectoryTier = 'tier-0' | 'council' | 'guardian' | 'infrastructure'
export type AgentRepo = 'runtime' | 'product'

export interface DirectoryAgent {
  /** API skill_name — used to join live telemetry from /api/v1/agents. */
  skillName: string
  displayName: string
  /** Greek-pantheon identity one-liner. */
  identity: string
  /** Short role description. */
  role: string
  /** Which codebase the agent runs in. */
  repo: AgentRepo
  /** AI model (or "Pure TypeScript" for non-LLM services). */
  model: string
  tier: DirectoryTier
  phase: AgentPhase
  /** Who the agent answers to. */
  reportsTo: string
  /** Human-readable schedule lines. Empty ⇒ event-driven / on-demand. */
  cron: string[]
  /** Upstream agents / APIs this agent reads from. */
  consumesFrom: string[]
  /** Downstream agents this agent feeds data to. */
  feedsInto: string[]
  accent: string
  emoji: string
}

export interface DirectorySection {
  tier: DirectoryTier
  label: string
  blurb: string
  agents: DirectoryAgent[]
}

// ─── Directory-only metadata, keyed by skill_name ─────────────────────────────

interface DirectoryMeta {
  identity: string
  cron: string[]
  consumesFrom: string[]
  feedsInto: string[]
}

const COUNCIL_DEFAULT_FEED = 'Zeus (business briefing + decision queue)'

const META: Record<string, DirectoryMeta> = {
  // ── Tier 0 — Executives ──
  'council-ceo': {
    identity: 'King of the gods — chairs the Council.',
    cron: [
      'Business morning briefing · 08:00',
      'Business EOD summary · 18:00',
      'Council health check · hourly',
    ],
    consumesFrom: ['All 18 Council agents (health + briefing sections)', 'Decision queue'],
    feedsInto: ['Mark (business briefing + decision queue)', 'Multi-Surface Router'],
  },
  'guardian-coo': {
    identity: 'Goddess of wisdom and strategy — orchestrates the fleet.',
    cron: [
      'Morning briefing · 08:00',
      'Midday check-in · 12:00',
      'EOD summary · 17:00',
      'Sunday weekly reflection · 09:00',
      'Overload check · hourly',
    ],
    consumesFrom: ['All Guardian agents', 'Fleet Intelligence Hub', 'Compliance KPI Engine'],
    feedsInto: ['Mark (operations briefing + decision queue)', 'Multi-Surface Router'],
  },

  // ── Council — Business Intel ──
  'finance-ops': {
    identity: 'God of wealth — guards the money.',
    cron: [
      'Weekly financial summary · Mon 09:02',
      'Daily dunning check · 10:00',
      'Monthly P&L · 1st 09:00',
    ],
    consumesFrom: ['Stripe webhooks (invoice / subscription events)'],
    feedsInto: ['Hera (churn alert on cancellation)', COUNCIL_DEFAULT_FEED],
  },
  'sales-pipeline': {
    identity: 'Goddess of the hunt — tracks every lead.',
    cron: ['Pipeline scan · 2-hourly, 08:00–18:00 weekdays'],
    consumesFrom: ['Product API bridge (trials, signups)', 'Mnemosyne (market signal)'],
    feedsInto: ['Hera (handoff on conversion)', COUNCIL_DEFAULT_FEED],
  },
  'product-intel': {
    identity: 'Titan who brought fire — surfaces what to build next.',
    cron: [
      'Daily usage aggregation · 03:00',
      'Weekly product pulse (RICE) · Fri 09:00',
      'Weekly competitive gap analysis · Fri 09:00',
      'Monthly PMF assessment · 1st 09:00',
    ],
    consumesFrom: ['Product API bridge (feature usage)', 'Mnemosyne (competitor features)', 'Demeter (analytics)'],
    feedsInto: [COUNCIL_DEFAULT_FEED],
  },
  'data-analytics': {
    identity: 'Goddess of the harvest — turns raw events into metrics.',
    cron: ['Daily metrics snapshot · 05:00', 'Weekly analytics report · Mon 07:00'],
    consumesFrom: ['Athena activity log', 'Product API bridge'],
    feedsInto: ['Prometheus (usage trends)', COUNCIL_DEFAULT_FEED],
  },

  // ── Council — Platform Services ──
  'security-infra': {
    identity: 'God of strength — holds the perimeter.',
    cron: ['Daily security check · 07:00', 'SSL midday check · 13:00'],
    consumesFrom: ['SSL endpoints', 'Backup verification', 'Sentry'],
    feedsInto: ['Asclepius (P1 escalation)', COUNCIL_DEFAULT_FEED],
  },
  'dev-ops': {
    identity: 'God of medicine — keeps the agents healthy.',
    cron: [
      'Health check · every 15 min',
      'Agent execution monitoring · every 15 min',
      'Full daily health report · 07:00',
    ],
    consumesFrom: ['Athena activity log (agent telemetry)', 'Railway / Neon / Redis health'],
    feedsInto: ['Kratos (security incidents)', COUNCIL_DEFAULT_FEED],
  },
  'qa-testing': {
    identity: 'Spirit of excellence — proves it actually works.',
    cron: ['Daily QA health scan · 06:00', 'Weekly QA health report · Mon 06:15'],
    consumesFrom: ['Athena activity log', 'Asclepius (health signals)'],
    feedsInto: ['Prometheus (defect-driven backlog)', COUNCIL_DEFAULT_FEED],
  },

  // ── Council — Customer Lifecycle ──
  'customer-success': {
    identity: 'Queen of the gods — protects the customer relationship.',
    cron: ['Hourly health scan · 08:00–18:00', 'Morning briefing data prep · 07:45'],
    consumesFrom: ['Plutus (churn signal)', 'Product API bridge (account health)', 'Artemis (new conversions)'],
    feedsInto: ['Iris (driver comms hand-off)', COUNCIL_DEFAULT_FEED],
  },
  'content-engine': {
    identity: 'Muse of history — writes the record.',
    cron: ['Weekly content pipeline · Mon 09:00', 'Topic backlog refresh · Wed 09:00'],
    consumesFrom: ['Mnemosyne (topics + regulatory feed)', 'Forum monitoring'],
    feedsInto: ['Apollo (content calendar)', COUNCIL_DEFAULT_FEED],
  },
  'content-marketing': {
    identity: 'God of music and poetry — plans the campaigns.',
    cron: ['Weekly content planning (SEO calendar) · Mon 09:00'],
    consumesFrom: ['Clio (articles)', 'Tyche (keyword opportunities)'],
    feedsInto: ['Helios (social distribution)', COUNCIL_DEFAULT_FEED],
  },
  'social-media': {
    identity: 'God of the sun — radiates the brand.',
    cron: ['Daily social content review · 10:00'],
    consumesFrom: ['Apollo (content calendar)', 'Mnemosyne (trends)'],
    feedsInto: [COUNCIL_DEFAULT_FEED],
  },
  'communications-pr': {
    identity: 'Muse of eloquence — speaks for the brand.',
    cron: ['Weekly PR planning (media-outreach plan) · Fri 10:00'],
    consumesFrom: ['Apollo (announcements)', 'Mnemosyne (market signal)', 'Aether (partner news)'],
    feedsInto: [COUNCIL_DEFAULT_FEED],
  },
  'market-intel': {
    identity: 'Goddess of memory — never forgets a competitor move.',
    cron: [
      'Daily competitor scan · 07:05',
      'Weekly forum scan · Mon 07:05',
      'Weekly sentiment analysis · Mon 08:00',
      'Quarterly landscape report · 1st 09:00',
    ],
    consumesFrom: ['GOV.UK RSS', 'Competitor sources', 'Industry forums'],
    feedsInto: ['Artemis (sales intel)', 'Prometheus (competitive gaps)', 'Clio (topics)', COUNCIL_DEFAULT_FEED],
  },
  'eu-jurisdiction': {
    identity: 'Goddess of divine law — reads the rules abroad.',
    cron: ['Weekly EU jurisdiction scan · Wed 08:00'],
    consumesFrom: ['EU regulatory sources (per country_code)'],
    feedsInto: ['Janus (registry connectors)', COUNCIL_DEFAULT_FEED],
  },
  'driver-comms': {
    identity: 'The swift messenger — carries every driver notice.',
    cron: [
      'Daily driver reminders · 07:30',
      'Event-driven: guardian/defect-raised, guardian/infringement-detected',
    ],
    consumesFrom: ['Product API bridge (due reminders, channel prefs, tenant jurisdiction)', 'Guardian fleet events'],
    feedsInto: ['Product delivery (SMS / WhatsApp via Inngest)', 'Mark (infringement-notice approvals)', COUNCIL_DEFAULT_FEED],
  },

  // ── Council — Partners ──
  partnerships: {
    identity: 'Primordial god of the upper air — runs the partner pipeline.',
    cron: [
      'Daily API changelog scan · 07:10',
      'Weekly partner pipeline review · Mon 09:04',
    ],
    consumesFrom: ['ETM reseller pipeline', 'Hecate (integration status)'],
    feedsInto: ['Mark (partner decisions)', COUNCIL_DEFAULT_FEED],
  },
  'integration-hub': {
    identity: 'Goddess of crossroads — the sole integration health monitor.',
    cron: ['Integration health sweep · every 15 min'],
    consumesFrom: ['DVSA MOT', 'DVLA VES', 'Stripe', 'Railway', 'Twilio', 'Meta WhatsApp', 'Resend', 'Cloudflare R2', 'Upstash Redis', 'Neon', 'Clerk', 'Inngest', 'Companies House'],
    feedsInto: ['Aether (integration status)', COUNCIL_DEFAULT_FEED],
  },
  'eu-connectors': {
    identity: 'God of gates and transitions — bridges EU registries.',
    cron: ['Weekly EU registry data refresh · Thu 07:00'],
    consumesFrom: ['CRO (IE)', 'KvK (NL)', 'Handelsregister (DE)', 'KRS (PL)', 'Themis (jurisdiction intel)'],
    feedsInto: ['Mark (jurisdiction go-live decisions)', COUNCIL_DEFAULT_FEED],
  },

  // ── Council — Knowledge ──
  'knowledge-base': {
    identity: 'Goddess of the hearth — keeps the institutional memory.',
    cron: ['Daily stale-content scan · 06:30'],
    consumesFrom: ['All Council agents (process + product knowledge)'],
    feedsInto: ['All Council agents (FAQ + reference)', COUNCIL_DEFAULT_FEED],
  },
  'lead-gen-seo': {
    identity: 'Goddess of fortune — finds the next customer.',
    cron: ['Daily lead-gen + SEO analysis · 08:00'],
    consumesFrom: ['Search / SEO signals', 'Mnemosyne (market)'],
    feedsInto: ['Artemis (qualified leads)', 'Apollo (keyword opportunities)', COUNCIL_DEFAULT_FEED],
  },
}

// Guardian + infrastructure metadata. These agents run in tmagent-product on
// Inngest, so their schedules are described at the repo level rather than
// transcribed from skill configs that don't live here.
const GUARDIAN_DEFAULT_FEED = 'Athena (operations briefing + decision queue)'
const INNGEST_NOTE = 'Event-driven · Inngest (tmagent-product)'

const GUARDIAN_META: Record<string, DirectoryMeta> = {
  'compliance-officer': {
    identity: 'Goddess of justice — the compliance backbone.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['DVSA / DVLA via product API', 'Fleet Intelligence Hub'],
    feedsInto: ['Compliance KPI Engine', GUARDIAN_DEFAULT_FEED],
  },
  'maintenance-scheduling': {
    identity: 'God of the forge — keeps vehicles roadworthy.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Fleet Intelligence Hub', 'DVSA MOT data'],
    feedsInto: ['Daedalus (workshop)', GUARDIAN_DEFAULT_FEED],
  },
  'defect-triage': {
    identity: 'The hundred-eyed giant — sees every defect.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Driver defect reports', 'Fleet Intelligence Hub'],
    feedsInto: ['Hephaestus (maintenance)', 'Daedalus (workshop)', GUARDIAN_DEFAULT_FEED],
  },
  'workshop-ops': {
    identity: 'Master craftsman — runs the workshop.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Argus (defects)', 'Hephaestus (schedule)', 'Poros (parts)'],
    feedsInto: [GUARDIAN_DEFAULT_FEED],
  },
  'fleet-finance': {
    identity: 'Titan of intellect — the fleet cost brain.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Workshop costs', 'Parts spend', 'Compliance KPI Engine'],
    feedsInto: [GUARDIAN_DEFAULT_FEED],
  },
  'regulatory-monitoring': {
    identity: 'Goddess of truth — watches the regulators.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['GOV.UK / DVSA bulletins'],
    feedsInto: ['Dike (compliance)', GUARDIAN_DEFAULT_FEED],
  },
  'document-generation': {
    identity: 'Founder of Thebes — the scribe.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Dike (compliance state)', 'Fleet data'],
    feedsInto: ['Iris (delivery)', GUARDIAN_DEFAULT_FEED],
  },
  iris: {
    identity: 'Messenger goddess — talks to drivers.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Cadmus (documents)', 'Hera (CS hand-off)'],
    feedsInto: ['Drivers (email / SMS)', GUARDIAN_DEFAULT_FEED],
  },
  'parts-procurement': {
    identity: 'God of plenty — sources the parts.',
    cron: [INNGEST_NOTE],
    consumesFrom: ['Daedalus (workshop demand)'],
    feedsInto: ['Coeus (cost)', GUARDIAN_DEFAULT_FEED],
  },
  nemesis: {
    identity: 'Goddess of retribution — catches anomalies.',
    cron: ['Specced — not yet built'],
    consumesFrom: ['All Guardian telemetry'],
    feedsInto: [GUARDIAN_DEFAULT_FEED],
  },
  chiron: {
    identity: 'The wise centaur — trains drivers.',
    cron: ['Specced — not yet built'],
    consumesFrom: ['Driver CPC records'],
    feedsInto: [GUARDIAN_DEFAULT_FEED],
  },
  proteus: {
    identity: 'The shape-shifting sea god — migrates data.',
    cron: ['Specced — not yet built'],
    consumesFrom: ['Legacy fleet systems'],
    feedsInto: [GUARDIAN_DEFAULT_FEED],
  },
  'fleet-intelligence-hub': {
    identity: 'The central nervous system — routes every fleet event.',
    cron: ['Event-driven state machine'],
    consumesFrom: ['All Guardian agents (events)', 'Product fleet data'],
    feedsInto: ['All Guardian agents (priority routing)', 'Athena'],
  },
  'compliance-kpi-engine': {
    identity: 'The scorekeeper — computes DVSA Earned Recognition KPIs.',
    cron: ['On-demand service'],
    consumesFrom: ['Dike (compliance)', 'Fleet data'],
    feedsInto: ['Coeus (cost)', 'Athena'],
  },
}

const DEFAULT_META: DirectoryMeta = {
  identity: '—',
  cron: [],
  consumesFrom: [],
  feedsInto: [],
}

// ─── Build the directory from the org roster ──────────────────────────────────

function repoFor(tier: DirectoryTier): AgentRepo {
  return tier === 'council' ? 'runtime' : tier === 'guardian' || tier === 'infrastructure' ? 'product' : 'runtime'
}

function reportsToFor(tier: DirectoryTier): string {
  switch (tier) {
    case 'tier-0': return 'Mark'
    case 'council': return 'Zeus'
    default: return 'Athena'
  }
}

function tierFor(agent: OrgAgent): DirectoryTier {
  if (agent.phase === 'infrastructure') return 'infrastructure'
  return agent.branch === 'council' ? 'council' : 'guardian'
}

function toDirectoryAgent(agent: OrgAgent): DirectoryAgent {
  const tier = tierFor(agent)
  const meta = META[agent.skillName] ?? GUARDIAN_META[agent.skillName] ?? DEFAULT_META
  return {
    skillName: agent.skillName,
    displayName: agent.displayName,
    identity: meta.identity,
    role: agent.role,
    repo: repoFor(tier),
    model: agent.model,
    tier,
    phase: agent.phase,
    reportsTo: reportsToFor(tier),
    cron: meta.cron,
    consumesFrom: meta.consumesFrom,
    feedsInto: meta.feedsInto,
    accent: agent.accent,
    emoji: agent.emoji,
  }
}

// Tier 0 — the two executives. They live in the branch headers of agent-org.ts,
// not in the agent arrays, so they are constructed here directly.
const TIER_0: DirectoryAgent[] = [
  {
    skillName: 'council-ceo',
    displayName: 'Zeus',
    identity: META['council-ceo'].identity,
    role: 'Council CEO · Business',
    repo: 'runtime',
    model: 'Sonnet 4.6',
    tier: 'tier-0',
    phase: 'live',
    reportsTo: 'Mark',
    cron: META['council-ceo'].cron,
    consumesFrom: META['council-ceo'].consumesFrom,
    feedsInto: META['council-ceo'].feedsInto,
    accent: '#fbbf24',
    emoji: '⚡',
  },
  {
    skillName: 'guardian-coo',
    displayName: 'Athena',
    identity: META['guardian-coo'].identity,
    role: 'Guardian COO · Fleet Operations',
    repo: 'product',
    model: 'Sonnet 4.6',
    tier: 'tier-0',
    phase: 'live',
    reportsTo: 'Mark',
    cron: META['guardian-coo'].cron,
    consumesFrom: META['guardian-coo'].consumesFrom,
    feedsInto: META['guardian-coo'].feedsInto,
    accent: '#22d3ee',
    emoji: '🦉',
  },
]

const ALL_ORG_AGENTS: OrgAgent[] = ORG_BRANCHES.flatMap((b) => b.groups.flatMap((g) => g.agents))

const COUNCIL_AGENTS = ALL_ORG_AGENTS.filter((a) => tierFor(a) === 'council').map(toDirectoryAgent)
const GUARDIAN_AGENTS = ALL_ORG_AGENTS.filter((a) => tierFor(a) === 'guardian').map(toDirectoryAgent)
const INFRASTRUCTURE_AGENTS = ALL_ORG_AGENTS.filter((a) => tierFor(a) === 'infrastructure').map(toDirectoryAgent)

export const DIRECTORY_SECTIONS: DirectorySection[] = [
  {
    tier: 'tier-0',
    label: 'Tier 0 · Executives',
    blurb: 'Zeus and Athena — peers under Mark (DEC-021)',
    agents: TIER_0,
  },
  {
    tier: 'council',
    label: 'Council · Business (Zeus)',
    blurb: 'Business agents on the Athena Runtime',
    agents: COUNCIL_AGENTS,
  },
  {
    tier: 'guardian',
    label: 'Guardians · Fleet Ops (Athena)',
    blurb: 'Product agents on Inngest (tmagent-product)',
    agents: GUARDIAN_AGENTS,
  },
  {
    tier: 'infrastructure',
    label: 'Infrastructure',
    blurb: 'Non-LLM services under Athena',
    agents: INFRASTRUCTURE_AGENTS,
  },
]

export const ALL_DIRECTORY_AGENTS: DirectoryAgent[] = DIRECTORY_SECTIONS.flatMap((s) => s.agents)

export function repoLabel(repo: AgentRepo): string {
  return repo === 'runtime' ? 'athena-runtime' : 'tmagent-product'
}
